import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { updateUserProfile, getUser } from "@/services/userService";
import { toast } from "sonner";
//import { type User } from "../../amplify/api/models";

interface UserProfileFormProps {
    username: string;
    onSuccess?: () => void;
}

export function UserProfileForm({ username, onSuccess }: UserProfileFormProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userData, setUserData] = useState<{
        email: string;
        givenName: string;
        familyName: string;
    }>({
        email: "",
        givenName: "",
        familyName: "",
    });

    useEffect(() => {
        const loadUser = async () => {
            try {
                setIsLoading(true);
                const user = await getUser(username);
                setUserData({
                    email: user.email || "",
                    givenName: user.givenName || "",
                    familyName: user.familyName || "",
                });
            } catch (error) {
                toast.error(`Failed to load user: ${(error as Error).message}`);
            } finally {
                setIsLoading(false);
            }
        };

        loadUser();
    }, [username]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await updateUserProfile({
                username,
                email: userData.email,
                givenName: userData.givenName || undefined,
                familyName: userData.familyName || undefined,
            });

            toast.success("User profile updated successfully");

            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            toast.error(
                `Failed to update profile: ${(error as Error).message}`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div>Loading user profile...</div>;
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    value={userData.email}
                    onChange={handleChange}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="givenName">Given Name</Label>
                    <Input
                        id="givenName"
                        name="givenName"
                        value={userData.givenName}
                        onChange={handleChange}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="familyName">Family Name</Label>
                    <Input
                        id="familyName"
                        name="familyName"
                        value={userData.familyName}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
        </form>
    );
}
